import { useMemo, useState } from "react"
import { Box, Button, CardContent, Collapse, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { graphic } from "echarts"
import ReactECharts from "echarts-for-react"
import { chartText } from "../lib/chartText"
import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"

dayjs.extend(isoWeek)

const formatUSD = (n = 0) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)

const getQuoteTotal = (quote) =>
    (quote?.lines || [])
        .filter((line) => {
            const type = line.type || line.rowType
            return type !== "subtotal" && type !== "runningsubtotal"
        })
        .reduce((sum, line) => sum + (Number(line.extPrice) || 0), 0)

const normalizeQuoteBaseNumber = (quoteNumber = "") =>
    String(quoteNumber || "").replace(/\s-\sRev\s\d+$/, "").trim()

const getCurrentYearWeekRange = () => ({
    start: dayjs().startOf("year"),
    end: dayjs().endOf("isoWeek"),
})

const buildNetTotalsByQuoteId = (quotes = []) => {
    const groups = new Map()

    for (const quote of quotes) {
        const opportunityId = quote?.opportunityId ? String(quote.opportunityId) : ""
        const baseQuoteNumber = normalizeQuoteBaseNumber(quote?.quoteNumber)
        const groupKey = opportunityId ? `opp:${opportunityId}` : `quote:${baseQuoteNumber}`

        if (!groups.has(groupKey)) groups.set(groupKey, [])
        groups.get(groupKey).push(quote)
    }

    const netTotalsById = new Map()

    for (const groupQuotes of groups.values()) {
        const sortedQuotes = [...groupQuotes].sort((a, b) => {
            const revisionDiff = (Number(a?.revision) || 0) - (Number(b?.revision) || 0)
            if (revisionDiff !== 0) return revisionDiff

            return dayjs(a?.createdAt).valueOf() - dayjs(b?.createdAt).valueOf()
        })

        let previousTotal = 0

        for (const quote of sortedQuotes) {
            const currentTotal = getQuoteTotal(quote)
            const revision = Number(quote?.revision) || 0
            const netTotal = revision > 0 ? currentTotal - previousTotal : currentTotal

            netTotalsById.set(String(quote._id), netTotal)
            previousTotal = currentTotal
        }
    }

    return netTotalsById
}

const DailyQuoteTrendCard = ({ quotes = [] }) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const [collapsed, setCollapsed] = useState(false)
    const [startDate, setStartDate] = useState(() => getCurrentYearWeekRange().start.format("YYYY-MM-DD"))
    const [endDate, setEndDate] = useState(() => getCurrentYearWeekRange().end.format("YYYY-MM-DD"))

    const handleResetToCurrentYear = () => {
        const range = getCurrentYearWeekRange()
        setStartDate(range.start.format("YYYY-MM-DD"))
        setEndDate(range.end.format("YYYY-MM-DD"))
    }

    const chartData = useMemo(() => {
        const start = dayjs(startDate).startOf("day")
        const end = dayjs(endDate).endOf("day")

        if (!start.isValid() || !end.isValid() || start.valueOf() > end.valueOf()) {
            return {
                labels: [],
                weekRanges: [],
                totalSeries: [],
                netSeries: [],
                newQuoteSeries: [],
                grossRangeTotal: 0,
                netRangeTotal: 0,
                newQuoteRangeTotal: 0,
            }
        }

        const netTotalsByQuoteId = buildNetTotalsByQuoteId(quotes)

        const bucketStart = start.startOf("isoWeek")
        const bucketEnd = end.endOf("isoWeek")
        const weekRanges = []
        const grossTotalsByWeek = new Map()
        const netTotalsByWeek = new Map()
        const newQuotesByWeek = new Map()

        for (let current = bucketStart; current.valueOf() <= bucketEnd.valueOf(); current = current.add(1, "week")) {
            const key = current.format("YYYY-MM-DD")
            const displayStart = dayjs.max ? dayjs.max(current, start) : (current.valueOf() > start.valueOf() ? current : start)
            const rawEnd = current.endOf("isoWeek")
            const displayEnd = dayjs.min ? dayjs.min(rawEnd, end) : (rawEnd.valueOf() < end.valueOf() ? rawEnd : end)

            weekRanges.push({
                key,
                label: `W${weekRanges.length + 1}`,
                display: `${displayStart.format("MMM D")} - ${displayEnd.format("MMM D, YYYY")}`,
            })

            grossTotalsByWeek.set(key, 0)
            netTotalsByWeek.set(key, 0)
            newQuotesByWeek.set(key, 0)
        }

        for (const quote of quotes) {
            if (!quote?.createdAt) continue

            const createdAt = dayjs(quote.createdAt)
            if (!createdAt.isValid()) continue
            if (createdAt.valueOf() < start.valueOf() || createdAt.valueOf() > end.valueOf()) continue

            const weekKey = createdAt.startOf("isoWeek").format("YYYY-MM-DD")
            if (!grossTotalsByWeek.has(weekKey)) continue

            grossTotalsByWeek.set(weekKey, (grossTotalsByWeek.get(weekKey) || 0) + getQuoteTotal(quote))
            netTotalsByWeek.set(weekKey, (netTotalsByWeek.get(weekKey) || 0) + (netTotalsByQuoteId.get(String(quote._id)) || 0))

            if ((Number(quote?.revision) || 0) === 0) {
                newQuotesByWeek.set(weekKey, (newQuotesByWeek.get(weekKey) || 0) + getQuoteTotal(quote))
            }
        }

        const totalSeries = weekRanges.map((range) => grossTotalsByWeek.get(range.key) || 0)
        const netSeries = weekRanges.map((range) => netTotalsByWeek.get(range.key) || 0)
        const newQuoteSeries = weekRanges.map((range) => newQuotesByWeek.get(range.key) || 0)

        return {
            labels: weekRanges.map((range) => range.label),
            weekRanges,
            totalSeries,
            netSeries,
            newQuoteSeries,
            grossRangeTotal: totalSeries.reduce((sum, value) => sum + value, 0),
            netRangeTotal: netSeries.reduce((sum, value) => sum + value, 0),
            newQuoteRangeTotal: newQuoteSeries.reduce((sum, value) => sum + value, 0),
        }
    }, [endDate, quotes, startDate])

    const dataZoomRange = useMemo(() => {
        const totalWeeks = chartData.labels.length || 1
        const visibleWeeks = isMobile ? Math.min(12, totalWeeks) : Math.min(20, totalWeeks)
        const startIndex = Math.max(0, totalWeeks - visibleWeeks)

        return {
            start: (startIndex / totalWeeks) * 100,
            end: 100,
        }
    }, [chartData.labels.length, isMobile])

    const axisLabelInterval = useMemo(() => {
        const count = chartData.labels.length

        if (count <= 12) return 0
        if (isMobile) return Math.max(0, Math.ceil(count / 6) - 1)
        return Math.max(0, Math.ceil(count / 10) - 1)
    }, [chartData.labels.length, isMobile])

    const option = useMemo(
        () => ({
            backgroundColor: "#08111d",
            legend: {
                top: 4,
                right: 12,
                textStyle: {
                    color: "rgba(241, 245, 249, 0.82)",
                    fontSize: isMobile ? 10 : 11,
                },
                itemWidth: 18,
                itemHeight: 10,
                data: ["Gross Total", "Net Total", "New"],
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "cross",
                    label: {
                        backgroundColor: "#2d3138",
                        color: "#f8fafc",
                    },
                },
                    backgroundColor: "rgba(7, 13, 24, 0.96)",
                borderColor: "rgba(148, 163, 184, 0.2)",
                textStyle: {
                    color: "#f8fafc",
                },
                formatter: (params = []) => {
                    const weekLabel = chartData.weekRanges[params?.[0]?.dataIndex]?.display || params?.[0]?.axisValue || ""

                    const details = params
                        .filter((entry) =>
                            entry.seriesName === "Gross Total" ||
                            entry.seriesName === "Net Total" ||
                            entry.seriesName === "New"
                        )
                        .map((entry) => `${entry.marker} ${chartText(entry.seriesName)}: <strong>${chartText(formatUSD(entry.value || 0))}</strong>`)
                        .join("<br/>")

                    return `${chartText(weekLabel)}${details ? `<br/>${details}` : ""}`
                },
            },
            grid: {
                left: isMobile ? 44 : 58,
                right: isMobile ? 16 : 24,
                top: 46,
                bottom: isMobile ? 84 : 68,
                containLabel: true,
            },
            xAxis: {
                type: "category",
                boundaryGap: false,
                data: chartData.labels,
                axisLine: {
                    lineStyle: {
                        color: "rgba(255, 255, 255, 0.08)",
                    },
                },
                axisTick: { show: false },
                axisLabel: {
                    interval: axisLabelInterval,
                    rotate: isMobile ? 35 : 0,
                    fontSize: isMobile ? 10 : 12,
                    color: "rgba(226, 232, 240, 0.72)",
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: "rgba(255, 255, 255, 0.06)",
                    },
                },
            },
            yAxis: {
                type: "value",
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                    lineStyle: {
                        color: "rgba(255, 255, 255, 0.06)",
                    },
                },
                axisLabel: {
                    formatter: (value) => formatUSD(value),
                    fontSize: isMobile ? 10 : 12,
                    color: "rgba(226, 232, 240, 0.72)",
                },
            },
            dataZoom: [
                {
                    type: "inside",
                    start: dataZoomRange.start,
                    end: dataZoomRange.end,
                },
                {
                    type: "slider",
                    start: dataZoomRange.start,
                    end: dataZoomRange.end,
                    height: isMobile ? 18 : 22,
                    bottom: isMobile ? 12 : 18,
                    fillerColor: "rgba(255, 255, 255, 0.08)",
                    borderColor: "rgba(255, 255, 255, 0.05)",
                    backgroundColor: "rgba(8, 17, 29, 0.82)",
                    dataBackground: {
                        lineStyle: { color: "rgba(255, 255, 255, 0.22)" },
                        areaStyle: { color: "rgba(255, 255, 255, 0.05)" },
                    },
                    textStyle: {
                        color: "rgba(226, 232, 240, 0.7)",
                    },
                    handleStyle: {
                        color: "rgba(255, 255, 255, 0.2)",
                        borderColor: "rgba(255, 255, 255, 0.28)",
                    },
                },
            ],
            series: [
                {
                    name: "Gross Total",
                    type: "line",
                    smooth: true,
                    showSymbol: true,
                    symbol: "circle",
                    symbolSize: isMobile ? 5 : 6,
                    data: chartData.totalSeries,
                    lineStyle: {
                        width: isMobile ? 2.5 : 3,
                        color: "#69f0ae",
                        shadowBlur: 12,
                        shadowColor: "rgba(105, 240, 174, 0.24)",
                    },
                    itemStyle: {
                        color: "#69f0ae",
                        borderColor: "#69f0ae",
                        borderWidth: 0,
                    },
                    areaStyle: {
                        color: new graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: "rgba(105, 240, 174, 0.2)" },
                            { offset: 1, color: "rgba(105, 240, 174, 0.02)" },
                        ]),
                    },
                    z: 3,
                },
                {
                    name: "Net Total",
                    type: "line",
                    smooth: true,
                    showSymbol: true,
                    symbol: "circle",
                    symbolSize: isMobile ? 5 : 6,
                    data: chartData.netSeries,
                    lineStyle: {
                        width: isMobile ? 2.5 : 3,
                        color: "#ff7a7a",
                        shadowBlur: 12,
                        shadowColor: "rgba(255, 122, 122, 0.22)",
                    },
                    itemStyle: {
                        color: "#ff7a7a",
                        borderColor: "#ff7a7a",
                        borderWidth: 0,
                    },
                    z: 4,
                },
                {
                    name: "New",
                    type: "line",
                    smooth: true,
                    showSymbol: true,
                    symbol: "circle",
                    symbolSize: isMobile ? 5 : 6,
                    data: chartData.newQuoteSeries,
                    lineStyle: {
                        width: isMobile ? 2.5 : 3,
                        color: "#58a6ff",
                        shadowBlur: 12,
                        shadowColor: "rgba(88, 166, 255, 0.22)",
                    },
                    itemStyle: {
                        color: "#58a6ff",
                        borderColor: "#58a6ff",
                        borderWidth: 0,
                    },
                    z: 5,
                },
            ],
        }),
        [axisLabelInterval, chartData.labels, chartData.netSeries, chartData.newQuoteSeries, chartData.totalSeries, chartData.weekRanges, dataZoomRange.end, dataZoomRange.start, isMobile]
    )

    return (
        <div className="col-xl-12 col-md-12" style={{ marginBottom: "1em" }}>
            <div className="card">
                <div className="card-header">
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        spacing={1.5}
                    >
                        <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", minWidth: 0 }}>
                            <IconButton
                                onClick={() => setCollapsed((prev) => !prev)}
                                size="small"
                                sx={{ mr: 1, mt: 0.25 }}
                            >
                                {collapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Weekly Quote Trend
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Weekly marks for gross quoted value and net revision impact across the selected range.
                                </Typography>
                            </Box>
                        </Box>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Gross Total</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatUSD(chartData.grossRangeTotal)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Net Total</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: chartData.netRangeTotal < 0 ? "#b91c1c" : "inherit" }}>
                                    {formatUSD(chartData.netRangeTotal)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">New</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {formatUSD(chartData.newQuoteRangeTotal)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </div>
                <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                    <CardContent>
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1}
                            sx={{ mb: 2 }}
                        >
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Trend Start"
                                    value={dayjs(startDate)}
                                    format="MMM D, YYYY"
                                    onChange={(date) => {
                                        if (date && dayjs(date).isValid()) {
                                            setStartDate(dayjs(date).format("YYYY-MM-DD"))
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: {
                                                minWidth: { xs: "100%", sm: 180 },
                                                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                            },
                                        },
                                    }}
                                />
                                <DatePicker
                                    label="Trend End"
                                    value={dayjs(endDate)}
                                    format="MMM D, YYYY"
                                    onChange={(date) => {
                                        if (date && dayjs(date).isValid()) {
                                            setEndDate(dayjs(date).format("YYYY-MM-DD"))
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: {
                                                minWidth: { xs: "100%", sm: 180 },
                                                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Button
                                variant="outlined"
                                onClick={handleResetToCurrentYear}
                                sx={{ alignSelf: { xs: "stretch", md: "center" } }}
                            >
                                Reset to Current Year
                            </Button>
                        </Stack>
                        <Box
                            sx={{
                                background: "linear-gradient(180deg, #0b1524 0%, #07101b 100%)",
                                borderRadius: 3,
                                border: "1px solid rgba(255, 255, 255, 0.06)",
                                overflow: "hidden",
                                p: { xs: 1, sm: 1.5 },
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                            }}
                        >
                            <ReactECharts theme="v5"
                                option={option}
                                style={{ height: isMobile ? "340px" : "420px", width: "100%" }}
                                opts={{ renderer: "svg" }}
                            />
                        </Box>
                    </CardContent>
                </Collapse>
            </div>
        </div>
    )
}

export default DailyQuoteTrendCard
